import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router-dom';
import { AppButton, AppInput, AppSingleSelect } from '@pro-sto/ui';
import type { SelectOption } from '@pro-sto/ui';
import { ShiftAddEmployeePicker } from '@/components/ShiftAddEmployeePicker';
import { ShiftTemplateNameDialog } from '@/components/ShiftTemplateNameDialog';
import { ArrowLeftIcon, DotsThreeVerticalIcon, ExclamationMarkIcon, FloppyDiskIcon } from '@/components/icons';
import {
  authStore,
  operatingHoursStore,
  paymentSchemeTemplatesStore,
  shiftDraftStore,
  shiftTemplatesStore,
  shiftsStore,
} from '@/stores';
import {
  employeesShiftsPath,
  parseDateParam,
  plannedTimeWindow,
  toDateParam,
  type ShiftsDayLocationState,
  weekBounds,
} from '@/pages/EmployeesPage/components/ShiftsWeek/shiftWeekModel';
import type { ShiftDraftRow } from '@/stores/shiftDraftModel';
import {
  hasShiftTemplateErrors,
  toCreateShiftTemplatePayload,
  validateShiftTemplate,
  type ShiftTemplateDraftRow,
} from '@/stores/shiftTemplateModel';
import './ShiftEditPage.css';

const NO_TEMPLATE_OPTION: SelectOption = { label: 'Без шаблона', value: null };
const SELECT_DROPDOWN = { dropdownWidth: 'equal-toggle' as const };

const employeeName = (lastName: string, firstName: string) =>
  `${lastName} ${firstName}`.trim();

const initials = (lastName: string, firstName: string) =>
  `${lastName?.[0] ?? ''}${firstName?.[0] ?? ''}`.toUpperCase();

const TrashSimpleIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M4 5.5H16"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <path
      d="M8 5.4V4.2C8 3.54 8.54 3 9.2 3H10.8C11.46 3 12 3.54 12 4.2V5.4"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <path
      d="M5.4 5.5L6.15 16.05C6.2 16.7 6.75 17.2 7.4 17.2H12.6C13.25 17.2 13.8 16.7 13.85 16.05L14.6 5.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  </svg>
);

const uniqueMessages = (...messages: Array<string | undefined>): string[] =>
  [...new Set(messages.filter((message): message is string => Boolean(message)))];

const ShiftEditSkeleton: React.FC = () => (
  <div className="shift-edit-page__state" data-testid="shift-edit-loading">
    <span className="shift-edit-page__skeleton shift-edit-page__skeleton_header" />
    <span className="shift-edit-page__skeleton shift-edit-page__skeleton_field" />
    <span className="shift-edit-page__skeleton shift-edit-page__skeleton_block" />
    <span className="shift-edit-page__skeleton shift-edit-page__skeleton_row" />
    <span className="shift-edit-page__skeleton shift-edit-page__skeleton_row" />
    <span className="shift-edit-page__skeleton shift-edit-page__skeleton_row" />
  </div>
);

interface ShiftEditNoticeProps {
  message: string;
  onBack: () => void;
  onRetry?: () => void;
}

const ShiftEditNotice: React.FC<ShiftEditNoticeProps> = ({ message, onBack, onRetry }) => (
  <div className="shift-edit-page__state shift-edit-page__state_notice" data-testid="shift-edit-error">
    <ExclamationMarkIcon size={24} />
    <p className="shift-edit-page__submit-error">{message}</p>
    <div className="shift-edit-page__state-actions">
      <AppButton variant="secondary" onClick={onBack} data-testid="shift-edit-error-back">
        Назад
      </AppButton>
      {onRetry ? (
        <AppButton variant="secondary" onClick={onRetry} data-testid="shift-edit-retry">
          Повторить
        </AppButton>
      ) : null}
    </div>
  </div>
);

const ShiftEditPage: React.FC = observer(() => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const serviceCenterUuid = authStore.user?.service_center_uuid;
  const draft = shiftDraftStore.draft;
  const closed = draft?.status === 'closed';
  const leavingRef = useRef(false);
  const mountedRef = useRef(true);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [saveTemplateError, setSaveTemplateError] = useState<string | null>(null);

  const draftChangeKey = draft
    ? JSON.stringify({
        responsible: draft.responsibleEmployeeUuid,
        rows: draft.rows.map((row) => [
          row.employeeUuid,
          row.plannedStart,
          row.plannedEnd,
          row.paymentSchemeTemplateUuid,
        ]),
      })
    : '';

  useEffect(() => {
    setSaveTemplateError(null);
  }, [draftChangeKey]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!serviceCenterUuid) return;
    paymentSchemeTemplatesStore.setServiceCenterUuid(serviceCenterUuid);
    operatingHoursStore.loadOperatingHours(serviceCenterUuid);
    void shiftTemplatesStore.loadTemplates(serviceCenterUuid);
    shiftDraftStore.loadEmployees(serviceCenterUuid);
  }, [serviceCenterUuid]);

  useEffect(() => {
    if (!uuid) return;
    shiftDraftStore.loadDraft(uuid);
    return () => {
      const abandonUuid = shiftDraftStore.peekAbandonUuid();
      const date = shiftDraftStore.draft?.date;
      const center = authStore.user?.service_center_uuid;
      shiftDraftStore.discard();
      void (async () => {
        if (abandonUuid) {
          await shiftDraftStore.deleteAbandonedShift(abandonUuid);
        }
        if (leavingRef.current || !date || !center) return;
        const week = weekBounds(parseDateParam(date));
        await shiftsStore.load(week.from, week.to, center);
      })();
    };
  }, [uuid]);

  const leaveToDay = async () => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    await shiftDraftStore.abandonEmptyCreatedShiftIfNeeded();
    const date = shiftDraftStore.draft?.date;
    if (date && serviceCenterUuid) {
      const week = weekBounds(parseDateParam(date));
      await shiftsStore.load(week.from, week.to, serviceCenterUuid);
    }
    if (!mountedRef.current) return;
    if (!date) {
      navigate(-1);
      return;
    }
    const state: ShiftsDayLocationState = { stayOnDayDate: date };
    navigate(employeesShiftsPath('day', date), { replace: true, state });
  };

  const handleSave = async () => {
    if (leavingRef.current) return;
    const bounds = shiftDraftStore.draft
      ? plannedTimeWindow(
          parseDateParam(shiftDraftStore.draft.date),
          operatingHoursStore.regularSchedule,
          operatingHoursStore.specialDates,
        )?.bounds
      : null;
    const ok = await shiftDraftStore.save(bounds);
    if (!ok || leavingRef.current || !mountedRef.current) return;
    await leaveToDay();
  };

  const leaveWithoutDraft = () => {
    navigate(employeesShiftsPath('week', toDateParam(new Date())));
  };

  if (!uuid) {
    return (
      <div className="shift-edit-page" data-testid="shift-edit-page">
        <div className="shift-edit-page__container">
          <ShiftEditNotice message="Не указана смена" onBack={leaveWithoutDraft} />
        </div>
      </div>
    );
  }

  if (shiftDraftStore.isLoadingDraft && !draft) {
    return (
      <div className="shift-edit-page" data-testid="shift-edit-page">
        <div className="shift-edit-page__container">
          <ShiftEditSkeleton />
        </div>
      </div>
    );
  }

  if (shiftDraftStore.loadError && !draft) {
    return (
      <div className="shift-edit-page" data-testid="shift-edit-page">
        <div className="shift-edit-page__container">
          <ShiftEditNotice
            message={shiftDraftStore.loadError}
            onBack={leaveWithoutDraft}
            onRetry={() => shiftDraftStore.loadDraft(uuid)}
          />
        </div>
      </div>
    );
  }

  if (!draft) return null;

  const templateOptions: SelectOption[] = [
    NO_TEMPLATE_OPTION,
    ...shiftTemplatesStore.templates.map((template) => ({
      label: template.name,
      value: template.uuid,
    })),
  ];
  const selectedTemplate = draft.selectedTemplateUuid
    ? templateOptions.find((option) => option.value === draft.selectedTemplateUuid) ?? null
    : null;

  const rosterIds = new Set(
    draft.rows.map((row) => row.employeeUuid).filter((id): id is string => Boolean(id)),
  );
  const responsibleOptions: SelectOption[] = draft.rows.flatMap((row) => {
    if (!row.employeeUuid) return [];
    const employee = shiftDraftStore.employees.find((item) => item.uuid === row.employeeUuid);
    const label = employee
      ? employeeName(employee.last_name, employee.first_name)
      : row.employeeName;
    if (!label) return [];
    return [{ value: row.employeeUuid, label }];
  });
  const responsibleValue =
    responsibleOptions.find((option) => option.value === draft.responsibleEmployeeUuid) ?? null;

  const schemeOptions: SelectOption[] = paymentSchemeTemplatesStore.activeOptions;
  const templateChosen = Boolean(draft.selectedTemplateUuid);

  const templateDraftRows = (): ShiftTemplateDraftRow[] =>
    draft.rows.map((row) => ({
      key: row.key,
      employeeUuid: row.employeeUuid,
      position: row.position,
      plannedStart: row.plannedStart,
      plannedEnd: row.plannedEnd,
      paymentSchemeTemplateUuid: row.paymentSchemeTemplateUuid,
    }));

  const handleSaveAsTemplateClick = () => {
    const errors = validateShiftTemplate({
      responsibleEmployeeUuid: draft.responsibleEmployeeUuid,
      rows: templateDraftRows(),
    });
    if (hasShiftTemplateErrors(errors)) {
      const message =
        errors.form ||
        errors.responsible ||
        Object.values(errors.rows).flatMap((row) => Object.values(row)).find(Boolean) ||
        'Не удалось сохранить шаблон';
      setSaveTemplateError(message);
      return;
    }
    setSaveTemplateError(null);
    setNameDialogOpen(true);
  };

  const handleSaveAsTemplate = async (name: string) => {
    if (!serviceCenterUuid || !draft.responsibleEmployeeUuid) return;
    const payload = toCreateShiftTemplatePayload({
      name,
      serviceCenterUuid,
      responsibleEmployeeUuid: draft.responsibleEmployeeUuid,
      rows: templateDraftRows(),
    });
    const created = await shiftTemplatesStore.createTemplate(payload);
    if (!created) return;
    setNameDialogOpen(false);
    shiftDraftStore.setSelectedTemplate(created.uuid);
  };

  return (
    <div className="shift-edit-page" data-testid="shift-edit-page">
      <div className="shift-edit-page__container">
        <div className="shift-edit-page__header">
          <div className="shift-edit-page__header-left">
            <button
              type="button"
              className="shift-edit-page__back-button"
              onClick={leaveToDay}
              disabled={shiftDraftStore.isSaving}
              aria-label="Назад"
              data-testid="shift-edit-back"
            >
              <ArrowLeftIcon size={20} />
            </button>
            <h1 className="shift-edit-page__title">Редактировать смену</h1>
          </div>
          <div className="shift-edit-page__header-actions">
            <AppButton
              variant="secondary"
              onClick={leaveToDay}
              disabled={shiftDraftStore.isSaving}
              data-testid="shift-edit-cancel"
            >
              Отменить
            </AppButton>
            <AppButton
              variant="primary"
              iconRight={<FloppyDiskIcon size={16} />}
              onClick={handleSave}
              loading={shiftDraftStore.isSaving}
              disabled={closed}
              data-testid="shift-edit-save"
            >
              Сохранить
            </AppButton>
            <button
              type="button"
              className="shift-edit-page__menu"
              disabled
              aria-label="Меню смены"
              data-testid="shift-edit-menu"
            >
              <DotsThreeVerticalIcon size={20} />
            </button>
          </div>
        </div>

        {shiftDraftStore.submitError ? (
          <p
            className="shift-edit-page__submit-error"
            title={shiftDraftStore.submitError}
            data-testid="shift-edit-submit-error"
          >
            {shiftDraftStore.submitError}
          </p>
        ) : null}
        {shiftDraftStore.fieldErrors.form ? (
          <p className="shift-edit-page__submit-error" title={shiftDraftStore.fieldErrors.form}>
            {shiftDraftStore.fieldErrors.form}
          </p>
        ) : null}
        {saveTemplateError ? (
          <p className="shift-edit-page__submit-error" title={saveTemplateError}>
            {saveTemplateError}
          </p>
        ) : null}

        <section className="shift-edit-page__section">
          <div className="shift-edit-page__responsible shift-edit-page__field">
            <AppSingleSelect
              label="Ответственный"
              placeholder="Введите имя"
              options={responsibleOptions}
              value={responsibleValue}
              onChange={(option) =>
                shiftDraftStore.setResponsible((option?.value as string | null) ?? null)
              }
              error={shiftDraftStore.fieldErrors.responsible}
              disabled={closed}
              clearable
              baseDropdownProps={SELECT_DROPDOWN}
              data-testid="shift-edit-responsible"
            />
          </div>
        </section>

        <section className="shift-edit-page__section">
          <h2 className="shift-edit-page__section-title">Шаблон смены</h2>
          <p className="shift-edit-page__hint">
            Источник заполнит состав и общее время. После применения всё можно изменить.
          </p>
          <div className="shift-edit-page__template">
            <div className="shift-edit-page__template-select shift-edit-page__field">
              <AppSingleSelect
                placeholder="Без шаблона"
                options={templateOptions}
                value={selectedTemplate}
                onChange={(option) =>
                  shiftDraftStore.setSelectedTemplate((option?.value as string | null) ?? null)
                }
                disabled={closed || shiftTemplatesStore.templatesLoading}
                baseDropdownProps={SELECT_DROPDOWN}
                data-testid="shift-edit-template"
              />
            </div>
            <AppButton
              className="shift-edit-page__apply"
              variant="secondary"
              onClick={() => shiftDraftStore.applySelectedTemplate()}
              disabled={closed || shiftDraftStore.isSaving || !templateChosen}
              data-testid="shift-edit-apply"
            >
              Применить
            </AppButton>
            <AppButton
              className="shift-edit-page__save-template"
              variant="secondary"
              iconRight={<FloppyDiskIcon size={16} />}
              onClick={handleSaveAsTemplateClick}
              loading={shiftTemplatesStore.isSaving}
              disabled={shiftDraftStore.isSaving}
              data-testid="shift-edit-save-template"
            >
              Сохранить как шаблон
            </AppButton>
          </div>
          {shiftTemplatesStore.templatesLoading ? (
            <p className="shift-edit-page__hint" data-testid="shift-edit-templates-loading">
              Загрузка шаблонов...
            </p>
          ) : null}
          {shiftTemplatesStore.templatesError ? (
            <div className="shift-edit-page__inline-error" data-testid="shift-edit-templates-error">
              <p className="shift-edit-page__submit-error" title={shiftTemplatesStore.templatesError}>
                {shiftTemplatesStore.templatesError}
              </p>
              <AppButton
                variant="secondary"
                onClick={() =>
                  serviceCenterUuid &&
                  void shiftTemplatesStore.loadTemplates(serviceCenterUuid, { force: true })
                }
                disabled={shiftTemplatesStore.templatesLoading}
                data-testid="shift-edit-templates-retry"
              >
                Повторить
              </AppButton>
            </div>
          ) : null}
        </section>

        <section className="shift-edit-page__section">
          <h2 className="shift-edit-page__section-title">Состав смены</h2>
          <ShiftAddEmployeePicker
            takenIds={rosterIds}
            onPick={(employeeUuid) => shiftDraftStore.addEmployeeToRoster(employeeUuid)}
            disabled={Boolean(closed)}
            data-testid="shift-edit-add-member"
            loadingTestId="shift-edit-employees-loading"
            retryTestId="shift-edit-employees-menu-retry"
          />
          {shiftDraftStore.employeesError ? (
            <div className="shift-edit-page__inline-error" data-testid="shift-edit-employees-error">
              <p className="shift-edit-page__submit-error" title={shiftDraftStore.employeesError}>
                {shiftDraftStore.employeesError}
              </p>
              <AppButton
                variant="secondary"
                onClick={() =>
                  serviceCenterUuid &&
                  shiftDraftStore.loadEmployees(serviceCenterUuid, { force: true })
                }
                disabled={shiftDraftStore.employeesLoading}
                data-testid="shift-edit-employees-retry"
              >
                Повторить
              </AppButton>
            </div>
          ) : null}
          <div className="shift-edit-page__table">
            <div className="shift-edit-page__row shift-edit-page__row_head">
              <span>Сотрудник</span>
              <span>Роль</span>
              <span>Время</span>
              <span>Схема оплаты</span>
              <span />
            </div>
            {draft.rows.map((row) => (
              <MemberRow
                key={row.key}
                row={row}
                schemeOptions={schemeOptions}
                closed={closed}
                trashDisabled={draft.status !== 'created'}
              />
            ))}
          </div>
        </section>
      </div>

      <ShiftTemplateNameDialog
        isOpen={nameDialogOpen}
        isSaving={shiftTemplatesStore.isSaving}
        onSave={handleSaveAsTemplate}
        onCancel={() => setNameDialogOpen(false)}
      />
    </div>
  );
});

interface MemberRowProps {
  row: ShiftDraftRow;
  schemeOptions: SelectOption[];
  closed: boolean;
  trashDisabled: boolean;
}

const MemberRow: React.FC<MemberRowProps> = observer(({
  row,
  schemeOptions,
  closed,
  trashDisabled,
}) => {
  const errors = shiftDraftStore.fieldErrors.rows[row.key];
  const employee = shiftDraftStore.employees.find((item) => item.uuid === row.employeeUuid);
  const schemeValue =
    schemeOptions.find((option) => option.value === row.paymentSchemeTemplateUuid) ?? null;
  const name = employee
    ? employeeName(employee.last_name, employee.first_name)
    : row.employeeName ?? '';
  const position = employee?.position.name ?? row.position ?? '—';
  const avatar = employee
    ? initials(employee.last_name, employee.first_name)
    : initials(name.split(/\s+/)[0] ?? '', name.split(/\s+/)[1] ?? '');

  return (
    <div className="shift-edit-page__row" data-testid={`shift-edit-row-${row.key}`}>
      <div className="shift-edit-page__employee" data-testid={`shift-edit-employee-${row.key}`}>
        {name ? (
          <span className="shift-edit-page__avatar" aria-hidden="true">
            {avatar}
          </span>
        ) : null}
        <span
          className={`shift-edit-page__employee-name${name ? '' : ' shift-edit-page__employee-name_empty'}`}
          title={name || undefined}
        >
          {name || '—'}
        </span>
        {errors?.employee ? (
          <span className="shift-edit-page__row-error">{errors.employee}</span>
        ) : null}
      </div>
      <span
        className="shift-edit-page__position"
        title={position}
        data-testid={`shift-edit-position-${row.key}`}
      >
        {position}
      </span>
      <div className="shift-edit-page__time">
        <div className="shift-edit-page__time-fields">
          <div className="shift-edit-page__time-input">
            <AppInput
              size="M"
              value={row.plannedStart}
              placeholder="00:00"
              mask="00:00"
              lazy
              disabled={closed}
              error={Boolean(errors?.plannedStart)}
              onChange={(value) => shiftDraftStore.setRowTime(row.key, 'start', value)}
              onBlur={(event) => {
                const raw = event.currentTarget.value;
                window.setTimeout(() => {
                  shiftDraftStore.completeRowTime(row.key, 'start', raw);
                }, 0);
              }}
              data-testid={`shift-edit-start-${row.key}`}
            />
          </div>
          <span className="shift-edit-page__time-dash" aria-hidden="true">–</span>
          <div className="shift-edit-page__time-input">
            <AppInput
              size="M"
              value={row.plannedEnd}
              placeholder="00:00"
              mask="00:00"
              lazy
              disabled={closed}
              error={Boolean(errors?.plannedEnd)}
              onChange={(value) => shiftDraftStore.setRowTime(row.key, 'end', value)}
              onBlur={(event) => {
                const raw = event.currentTarget.value;
                window.setTimeout(() => {
                  shiftDraftStore.completeRowTime(row.key, 'end', raw);
                }, 0);
              }}
              data-testid={`shift-edit-end-${row.key}`}
            />
          </div>
        </div>
        {uniqueMessages(errors?.plannedStart, errors?.plannedEnd).map((message) => (
          <span
            key={message}
            className="shift-edit-page__time-error"
            title={message}
            data-testid={`shift-edit-time-error-${row.key}`}
          >
            {message}
          </span>
        ))}
      </div>
      <div className="shift-edit-page__scheme">
        <AppSingleSelect
          placeholder="Схема оплаты"
          options={schemeOptions}
          value={schemeValue}
          onChange={(option) =>
            shiftDraftStore.setRowScheme(row.key, (option?.value as string | null) ?? null)
          }
          error={errors?.scheme}
          disabled={closed}
          baseDropdownProps={SELECT_DROPDOWN}
          data-testid={`shift-edit-scheme-${row.key}`}
        />
      </div>
      <div className="shift-edit-page__row-actions">
        <button
          type="button"
          className="shift-edit-page__menu-row"
          disabled
          aria-label="Меню строки"
          data-testid={`shift-edit-row-menu-${row.key}`}
        >
          <DotsThreeVerticalIcon size={16} />
        </button>
        <button
          type="button"
          className="shift-edit-page__trash"
          onClick={() => shiftDraftStore.removeEmployeeRow(row.key)}
          disabled={trashDisabled || closed}
          aria-label="Убрать из смены"
          data-testid={`shift-edit-remove-${row.key}`}
        >
          <TrashSimpleIcon />
        </button>
      </div>
    </div>
  );
});

MemberRow.displayName = 'ShiftMemberRow';
ShiftEditPage.displayName = 'ShiftEditPage';

export default ShiftEditPage;
